import { ChevronDown, MessageCircleQuestion, Sparkles, Lightbulb } from "lucide-react";
import { SpeakableSchema } from "./SchemaMarkup";

/**
 * AEO Content — Long-form structured Q&A blocks optimized for:
 * - Google AI Overview / SGE
 * - ChatGPT browsing
 * - Perplexity AI
 * - Claude Web
 * - Voice assistants (Speakable schema)
 *
 * Renders visible, semantic HTML for humans + voice. JSON-LD FAQPage schema
 * is emitted by the PARENT page (HomePage, CategoryPage, SEOLandingPage) via
 * a single <FAQSchema /> — this component intentionally does NOT emit its own
 * FAQPage schema to avoid the "duplicate FAQPage" error in Google Search
 * Console (2026 policy: only ONE FAQPage per URL).
 */
export default function AEOContent({
  title = "Întrebări frecvente",
  subtitle,
  questions = [],
  variant = "default", // "default" | "compact" | "premium"
  className = ""
}) {
  if (!questions || questions.length === 0) return null;

  return (
    <section
      className={`relative ${className}`}
      data-testid="aeo-content"
    >
      {/* Speakable schema is still safe — only FAQPage microdata was the duplicate */}
      <SpeakableSchema cssSelectors={["[data-speakable]"]} />

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-start gap-3 mb-7">
          <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 flex items-center justify-center">
            <Lightbulb className="w-5 h-5 text-emerald-300" />
          </div>
          <div>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[11px] font-bold uppercase tracking-wider mb-2">
              <Sparkles className="w-3 h-3" />
              Răspunsuri rapide
            </span>
            <h2
              className="text-2xl sm:text-3xl font-black text-white tracking-tight"
              data-speakable="true"
            >
              {title}
            </h2>
            {subtitle && <p className="text-slate-400 text-sm mt-1.5 max-w-2xl">{subtitle}</p>}
          </div>
        </div>

        {/* Questions (pure visual — no schema microdata here; schema emitted once by page) */}
        <div className="space-y-3">
          {questions.map((q, i) => (
            <details
              key={i}
              className="group rounded-2xl bg-gradient-to-b from-white/[0.04] to-white/[0.01] border border-white/10 overflow-hidden hover:border-white/20 transition-colors"
              data-testid={`aeo-q-${i}`}
              open={i === 0}
            >
              <summary className="flex items-start justify-between gap-4 p-5 cursor-pointer list-none">
                <div className="flex items-start gap-3 flex-1">
                  <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 text-emerald-300 text-xs font-black">
                    {i + 1}
                  </span>
                  <h3
                    className="text-base sm:text-lg font-bold text-white leading-snug"
                  >
                    {q.question}
                  </h3>
                </div>
                <ChevronDown className="shrink-0 w-5 h-5 text-slate-400 group-open:rotate-180 transition-transform mt-0.5" />
              </summary>
              <div className="px-5 pb-5 pl-[60px]">
                <div
                  className="text-slate-300 leading-relaxed text-[15px] prose prose-invert max-w-none prose-a:text-emerald-300 prose-strong:text-white prose-strong:font-bold"
                  dangerouslySetInnerHTML={{ __html: q.answer }}
                />
                {q.relatedLinks && q.relatedLinks.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {q.relatedLinks.map((l, j) => (
                      <a
                        key={j}
                        href={l.href}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 text-xs text-emerald-200 hover:text-white transition-colors"
                      >
                        <MessageCircleQuestion className="w-3 h-3" />
                        {l.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============== Pre-built AEO question banks ==============

export const HOMEPAGE_AEO_QUESTIONS = [
  {
    question: "Care este cel mai bun site de anunțuri gratuite din România?",
    answer:
      "<strong>X67 Digital Media</strong> este platforma cea mai modernă de anunțuri gratuite din România, cu funcții premium pe care nu le găsești la <a href='/category/all'>OLX</a> sau Publi24: <strong>licitații live (Arena)</strong>, <strong>plată sigură via Escrow</strong> (banii sunt blocați până la livrare), <strong>verificare KYC</strong> a vânzătorilor, <strong>AI vision</strong> care îți generează descrierea anunțului direct din poze și <strong>mesagerie instant cu indicator de tipărire live</strong>. 100% gratuit, zero comision la vânzare."
  },
  {
    question: "Cum postez un anunț gratuit pe X67?",
    answer:
      "Postezi gratuit un anunț în <strong>3 minute</strong>: 1) Creează cont gratuit cu email sau Google. 2) Apasă butonul + din meniu și alege categoria (Auto, Imobiliare, Electronice, Locuri de muncă, Modă, Servicii, Animale). 3) Completează titlu, preț, locație și încarcă poze — apoi apasă <strong>'Completează cu AI'</strong> pentru ca inteligența artificială să-ți genereze automat descrierea premium din pozele tale. 4) Publică instant. Anunțul devine vizibil în câteva secunde și apare în Google indexat."
  },
  {
    question: "Este sigur să cumpăr de pe X67?",
    answer:
      "Da, X67 este una dintre cele mai sigure platforme de anunțuri din România, cu <strong>4 niveluri de protecție</strong>: <strong>(1) Verificare KYC</strong> — vânzătorii își confirmă identitatea cu act + selfie, validate de AI face-match. <strong>(2) Escrow</strong> — banii tăi sunt blocați la noi până la livrare confirmată. <strong>(3) Sistem reviews și rating</strong> pentru fiecare vânzător. <strong>(4) Raportare 1-click</strong> și echipă de moderare 24/7."
  },
  {
    question: "Ce taxă se percepe la vânzare pe X67?",
    answer:
      "<strong>Zero comision la vânzare</strong> pentru anunțuri standard. Postarea e 100% gratuită, fără limită de anunțuri. Ai opțional servicii premium (boost, promovare în top, escrow protecție extinsă) la prețuri minime — dar nimic din ce vinzi nu este taxat."
  },
  {
    question: "Cum funcționează Arena de licitații live?",
    answer:
      "<a href='/auctions'><strong>Arena de licitații</strong></a> este o premieră în România: orice vânzător își poate transforma anunțul în licitație live cu <strong>countdown vizibil, leaderboard în timp real (🥇🥈🥉), anti-sniping</strong> (bid în ultimele 30s extinde licitația automat) și opțional <strong>buy-now</strong>. Câștigătorul este conectat instant la Escrow pentru plată sigură. Adminii X67 pot modera dispute. Confetti la victorie!"
  },
  {
    question: "Pot vinde din toată țara sau doar din orașul meu?",
    answer:
      "Vinzi din <strong>orice localitate din România</strong> (368 localități indexate, 42 județe). Cumpărătorii filtrează după oraș, județ sau folosesc GPS-ul pentru 'Locația mea'. Platforma include <strong>Smart Location Picker</strong> cu căutare fuzzy + diacritice, harta interactivă (Leaflet/OpenStreetMap) și suport complet pentru Constanța, Cluj, Timișoara, Iași, Brașov etc."
  },
  {
    question: "Cum mă diferențiez de OLX, Publi24 și Lajumate?",
    answer:
      "X67 Digital aduce 5 inovații care nu există pe alte platforme românești: <strong>(1) AI Vision</strong> care îți generează anunțul din poze (Gemini 3 Flash). <strong>(2) Licitații live cu Escrow integrat</strong>. <strong>(3) Verificare KYC cu AI face-match</strong> (similaritate document/selfie ≥90% pentru aprobare instant). <strong>(4) Enterprise Seller API + Webhooks</strong> pentru magazine cu mii de produse. <strong>(5) PWA premium</strong> instalabilă cu push notifications, offline mode, chat real-time WebSocket."
  }
];

export const CATEGORY_AEO_QUESTIONS = (categoryName, citySlug) => [
  {
    question: `Cum găsesc cele mai bune oferte ${categoryName}${citySlug ? ` în ${citySlug}` : ""} pe X67?`,
    answer: `Pentru a găsi cele mai bune oferte ${categoryName}${citySlug ? ` în ${citySlug}` : ""}, folosește <strong>filtrele inteligente</strong> din partea stângă: subcategoria, intervalul de preț (RON sau EUR), localitatea exactă și sortarea după 'Cele mai noi' sau 'Preț: mic-mare'. Anunțurile <strong>verificate KYC</strong> au bifa verde lângă vânzător — recomandate pentru tranzacții sigure. Salvează căutarea pentru a primi alerte email instant când apar anunțuri noi.`
  },
  {
    question: `Sunt verificați vânzătorii ${categoryName}?`,
    answer: `Da. Toți vânzătorii pot aplica pentru badge-ul <strong>'Identitate Verificată'</strong> printr-un proces KYC scurt: încarcă document (CI/pașaport/permis) + selfie cu documentul. AI-ul nostru (Gemini Vision) analizează automat similaritatea facială și confirmă 90%+ înainte de aprobare manuală. Anunțurile vânzătorilor verificați primesc <strong>+30% încredere</strong> și conversion rate superior.`
  },
  {
    question: `Pot face o ofertă mai mică decât prețul cerut?`,
    answer: `Absolut. Pe orice anunț cu preț afișat (nu 'Negociabil'), apasă butonul <strong>'Fă o ofertă'</strong>. Introdu suma propusă (RON sau EUR), adaugă opțional un mesaj scurt și trimite. Vânzătorul primește notificare instant și poate accepta, refuza sau contra-oferta. Toate ofertele sunt urmărite în secțiunea <a href='/offers'>Ofertele mele</a>.`
  },
  {
    question: `Cum mă protejez la cumpărarea unui produs ${categoryName}?`,
    answer: `Folosește <strong>plata Escrow</strong>: când plătești, banii sunt blocați la X67 (nu la vânzător) până când confirmi că ai primit produsul în condițiile descrise. Dacă apare o dispută, echipa de moderare X67 analizează probele (poze, AWB, conversații) și decide refund sau eliberare fonduri. Combinat cu <strong>vânzător verificat KYC</strong> + <strong>rating 4.5+ stele</strong>, riscul scade aproape la zero.`
  }
];
